import React, { useEffect, useState } from "react";
import { Box, Grid, Button, Typography} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import NoData from "utils/NoData";
import {inboundFields, outboundFields} from 'config/index';
import InspectionRecordsHeader from "./InspectionRecordsHeader";
import { optionsAvailable } from "pages/AddWorker/components/utils";
import { endOfWeek, startOfWeek } from "date-fns";
import { useTranslation } from "react-i18next";
import { mkConfig, generateCsv, download} from "export-to-csv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import firebase from "firebase/app";
import "firebase/firestore";

dayjs.extend(isoWeek);

// 🔹 Firebase v8 init (make sure you initialized firebase elsewhere already)
const db = firebase.firestore();

export default function InspectionTable() {
  const { t } = useTranslation();
  const inspectionTypeOptions = ['inbound', 'outbound'];
  const [startDate, setStartDate] = useState(startOfWeek(new Date()));
  const [month, setMonth] = useState(new Date());
  const [view, setView] = useState(optionsAvailable[0]);
  const [inspectionType, setInspectionType] = useState(inspectionTypeOptions[0]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fields = inspectionType === "inbound" ? inboundFields : outboundFields;

  useEffect(()=>{
    setData([]);
     console.log("headerFields useEffect", startDate, inspectionType, view)
    if(startDate && inspectionType && view){
      fetchData();
    }
  },[startDate, inspectionType, view, month]);

  const columns = fields.map((f) => ({
    header: f.label,
    accessorKey: f.value,
  }));

    const handleExportData = () =>{
      console.log("rowData", data);
      const columnHeaders =  columns.map(field => ({displayLabel: field.header, key: field.accessorKey}));
        const csvConfig = mkConfig({
          fieldSeparator:',',
          decimalSeparator: '.',
          columnHeaders: [...columnHeaders],
          filename: inspectionType === "inbound"
            ? `Inbound Records ${dayjs().format("YYYY-MM-DD_HH-mm")}.csv`
            : `Outbound Records ${dayjs().format("YYYY-MM-DD_HH-mm")}.csv`
        })
      
      const csv = generateCsv(csvConfig)(data);
      download(csvConfig)(csv);
    }

  const fetchData = async () => {
    console.log("headerFields fetchData", startDate, inspectionType, view)
    setLoading(true);

    try {
      let start, end;

      if (view === "Weekly") {
        start = dayjs(startDate).startOf("week").toDate();
        end = dayjs(startDate).endOf("week").toDate();
      } else {
        start = dayjs(startDate).startOf("month").toDate();
        end = dayjs(startDate).endOf("month").toDate();
      }

      const snapshot = await db
        .collection("vehicleInspectionChecklistForm")
        .where("inspectionType", "==", inspectionType)
       .where("actionTS", ">=", start)
        .where("actionTS", "<=", end)
        .get();

     const docs = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        docId: doc.id,
        ...data,
        actionTS: data.actionTS?.seconds
          ? dayjs.unix(data.actionTS.seconds).format("YYYY-MM-DD HH:mm:ss")
          : "", // fallback if null
      };
    });

      console.log(docs)
     setData(docs);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
    setLoading(false);
  };

  // 🔹 Create MRT instance
  const table = useMaterialReactTable({
    columns,
    data,
    enablePagination: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableStickyHeader: true,
    localization:{
      noRecordsToDisplay: <NoData/>,
      noResultsFound: <NoData/>
    },
    state:{
      isLoading: loading,
      showProgressBars: loading,
      columnOrder: columns.map(col => col.id),
    },
    muiCircularProgressProps:{
      sx:{
          ".MuiCircularProgress-circle":{
            transition:'none'
          }
        },
        variant: 'determinate'
    },
    muiSkeletonProps:{
        animation:'wave',
        height: 20
    },
    enableStickyFooter: true,
    muiPaginationProps:{
      rowsPerPageOptions: [10, 25, 50, 100],
      showRowsPerPage: true
    },
    initialState: {
      density: "compact",
      columnPinning: {
          right: ['mrt-row-actions'],
      },
      pagination:{
        pageSize: 50
      },
      columnOrder: columns.map(col => col.id),
    },
    muiTableContainerProps: {
      sx: {
          minHeight: `calc(100vh - 400px)`,
          maxHeight: `calc(100vh - 220px)`
      },
    },
     renderTopToolbarCustomActions:({table}) =>{
      return (
        <Grid container style={{
          gap:"20px",
          alignItems:"flex-end",
          margin:"5px 20px 0 0px",
          justifyContent:"space-between",
        }}>
          <Typography variant="h6" sx={{color: "#58595B"}}>Vehicle Inspection Records</Typography>
          <Button
            disabled={table.getPrePaginationRowModel().rows.length === 0}
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
          onClick={()=>
            handleExportData(table.getPrePaginationRowModel().rows)
          }
          startIcon={<FileDownloadIcon />}
        >
          Export
        </Button>
        </Grid>
      )
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2 }}>
        {/* Filters */}
        <InspectionRecordsHeader 
          view={view}
          setView={setView}
          month={month}
          setMonth={setMonth}
          setInspectionType={setInspectionType}
          startDate={startDate}
          setStartDate={setStartDate}
        />
        {/* Table */}
        <MaterialReactTable table={table} />
      </Box>
    </LocalizationProvider>
  );
}
